/* eslint-disable max-len */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import apiClient from '../../common/functions/api_axios_instance.function';
import { handleApiError } from '../../common/functions/errorHandler.function';
import { notify } from '../../common/functions/react-toastify.function';
import {
  startInvoice,
  stopInvoice,
  rejectInvoice,
  approveInvoice,
  getCanStartStopped,
} from './functions/update_invoice_status.function';
import { getApproverRoles } from '../POWorkflowManagement/functions/get_approver_roles.function';
import { getDocumentBlob, revokeBlobUrl } from './functions/get_document_blob.function';
import { getApprovalPersonInvoiceAuditLogs } from './functions/get_approval_person_invoice_audit_logs.function';
import InvoiceHeader from './components/invoice_header.component';
import InvoiceDetailsCard from './components/invoice_details_card.component';
import BreakdownTable from './components/breakdown_table.component';
import CommentsSection from './components/comments_section.component';
import DocumentsList from './components/documents_list.component';
import AddMissingDocumentsSection from './components/add_missing_documents_section.component';
import InvoicePreview from './components/invoice_preview.component';
import DocumentPreview from './components/document_preview.component';
import InvoiceHistoryModal from './components/invoice_history_modal.component';
import { runWithUnsavedCommentsGuard } from '../../common/functions/unsaved_comments_guard.function';
import { buildPoInvoiceSummary } from './utils/po_invoice_summary.util';
import { getAllVendorInvoicesApprovalPerson } from './functions/get_all_vendor_invoices_approval_person.function';
import { getLoginState } from '../logIn/protected_Routes_component';
import {
  getCurrentApproverEmail,
  getPreviousApproverEmailFromAuditLogs,
} from '../../common/functions/derive_invoice_approver_emails.function';

const InvoiceApproveRejectForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  // Support both legacy :invoiceNumber param and new splat route
  const rawParam = params.invoiceNumber || params['*'] || '';
  let invoiceNumber = rawParam;
  try {
    invoiceNumber = decodeURIComponent(rawParam);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      'Failed to decode approvalPersons invoiceNumber param, using raw value:',
      rawParam,
      e
    );
    invoiceNumber = rawParam;
  }
  const passedInvoiceData = location.state?.invoiceData; // Get invoice data from navigation state
  const fromTab = location.state?.fromTab || '';
  const isViewOnlyFromAllInvoices = fromTab === 'all-invoices-full';

  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!getLoginState()) {
      return;
    }
    if (!permissionNames.includes('Approval Persons Invoice Management')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  const [comment, setComment] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deepLinkResolved, setDeepLinkResolved] = useState(false);

  // Data states
  const [invoiceData, setInvoiceData] = useState(null);
  const [breakdownData, setBreakdownData] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null); // 'approve', 'reject', 'revert', 'stop', 'start' // 'revert', 'stop', 'start' commented out
  const [approverRoles, setApproverRoles] = useState([]);
  const [filteredApproverRoles, setFilteredApproverRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [assigningRole, setAssigningRole] = useState(false);
  const [userCanStartStopped, setUserCanStartStopped] = useState(false);
  const commentsSectionRef = useRef(null);
  const [poInvoicesForSummary, setPoInvoicesForSummary] = useState([]);

  const [invoiceSource, setInvoiceSource] = useState(passedInvoiceData);

  useEffect(() => {
    setInvoiceSource(passedInvoiceData || null);
  }, [passedInvoiceData]);

  // If user opened this page directly (e.g., from email deep link), fetch invoice details by invoiceNumber.
  useEffect(() => {
    const fetchInvoiceForDeepLink = async () => {
      if (passedInvoiceData) {
        setDeepLinkResolved(true);
        return;
      }
      if (invoiceSource) {
        setDeepLinkResolved(true);
        return;
      }
      if (!invoiceNumber) {
        setDeepLinkResolved(true);
        return;
      }

      setError(null);
      setLoading(true);
      setDeepLinkResolved(false);
      try {
        const encoded = encodeURIComponent(invoiceNumber);
        const resp = await apiClient.get(
          `/approvalPersonInvoiceManagement/invoice/details/${encoded}`
        );
        const invoice =
          resp?.data?.response != null ? resp.data.response : resp?.data?.data || resp?.data;
        if (!invoice) {
          setError('Invoice data not found. Please go back and try again.');
          return;
        }
        setInvoiceSource(invoice);
      } catch (err) {
        // Don't show toast here; this page already renders an on-screen error message.
        // eslint-disable-next-line no-console
        console.error('Failed to fetch invoice details for deep link:', err);
        setError('Invoice data not found. Please go back and try again.');
      } finally {
        setDeepLinkResolved(true);
        setLoading(false);
      }
    };

    fetchInvoiceForDeepLink();
    // Only rerun when invoiceNumber changes / state changes
  }, [invoiceNumber, passedInvoiceData, invoiceSource]);

  const invoicePayload = invoiceSource || passedInvoiceData;

  const initialDocSelectionRef = useRef(false);
  const lastLoadedInvoiceIdRef = useRef(null);

  const isDeepLinkOpen = !passedInvoiceData && !!invoiceNumber;
  // In deep-link mode, keep showing loader until we either have invoiceData OR we have a real error.
  // This prevents a brief "not found" flash between fetch completion and invoiceData mapping.
  const shouldBlockErrorUiForDeepLink = isDeepLinkOpen && !error && !invoiceData;

  useEffect(() => {
    initialDocSelectionRef.current = false;
    lastLoadedInvoiceIdRef.current = null;
  }, [invoiceNumber]);

  // Get current invoice number
  const currentInvoiceNumber =
    invoicePayload?.invoice_number || invoicePayload?.invoiceNumber || invoiceNumber || '';

  // Prefer id for mutations / audit logs (unique even when invoice_number is duplicated)
  const currentInvoiceId = invoicePayload?.id ?? invoicePayload?._originalData?.id ?? null;

  // Helper function to transform audit logs for ActivityTimeline
  const transformAuditLogsForTimeline = (logs) => {
    // Filter out SYSTEM entries - don't show them in invoice tracking
    // Filter out SYSTEM entries - don't show automated assignment / system rows in tracking
    const filteredLogs = (logs || []).filter(
      (log) => (log.performed_by_role || '').toUpperCase() !== 'SYSTEM'
    );
    return filteredLogs.map((log) => {
      // Create human-readable summary from action_code and action_details
      let summary = '';
      const actionCode = log.action_code || '';
      const actionDetails = log.action_details || {};

      switch (actionCode) {
        case 'CREATED':
          summary = 'Invoice created';
          if (actionDetails.vendor) {
            summary += ` by ${actionDetails.vendor}`;
          }
          break;
        case 'ASSIGNED':
          summary = 'Invoice assigned';
          if (log.assigned_role?.name) {
            summary += ` to ${log.assigned_role.name}`;
          } else if (actionDetails.assigned_to_role_id) {
            summary += ` to role ID ${actionDetails.assigned_to_role_id}`;
          }
          break;
        case 'APPROVED':
          summary = 'Invoice approved';
          break;
        case 'REJECTED':
          summary = 'Invoice rejected';
          break;
        case 'SUBMITTED':
          summary = 'Invoice submitted';
          break;
        case 'UPDATED':
          summary = 'Invoice updated';
          break;
        default:
          summary = actionCode ? `Invoice ${actionCode.toLowerCase()}` : 'Invoice action';
      }

      // Get the role name, show "Vendor" if role is "USER"
      const roleName = log.performed_by_role || 'Unknown';
      const displayName = roleName === 'USER' ? 'Vendor' : roleName;

      return {
        timeStamp: log.created_at || '',
        username: displayName,
        summary: summary,
        details: actionDetails,
      };
    });
  };

  // Helper function to map invoice data
  const mapInvoiceData = (invoiceData) => {
    const formatDate = (dateString) => {
      if (!dateString) {
        return '';
      }
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
      } catch (e) {
        return dateString;
      }
    };

    const poSummary = buildPoInvoiceSummary({
      rawInvoice: invoiceData,
      poInvoicesList: poInvoicesForSummary,
    });

    const mappedInvoiceData = {
      invoiceNumber: invoiceData.invoice_number || invoiceData.invoiceNumber || '',
      title: invoiceData.invoice_number || invoiceData.invoiceNumber || '',
      invoiceNo: invoiceData.invoice_number || invoiceData.invoiceNumber || '',
      category: invoiceData.category || '',
      vendorName: invoiceData.vendor || '',
      invoiceDate: formatDate(
        invoiceData.created_at || invoiceData.updated_at || invoiceData.invoice_date
      ),
      region: invoiceData.region || '',
      poNumber: invoiceData.po_number || invoiceData.poNumber || '',
      poStatus: 'Active', // You may need to add this field to API response
      approvalStatus: invoiceData.status
        ? invoiceData.status.charAt(0).toUpperCase() + invoiceData.status.slice(1).toLowerCase()
        : '',
      status: invoiceData.status || '',
      previousApprover:
        invoiceData.previous_assigned_role?.name || invoiceData.previousApprover || '',
      currentApprover: invoiceData.assigned_role?.name || invoiceData.currentApprover || '',
      vendorRemarks: invoiceData.vendor_remarks || invoiceData.vendorRemarks || '',
      poTotalAmountFormatted: poSummary.poTotalAmountFormatted,
      alreadyClaimedAmountFormatted: poSummary.alreadyClaimedAmountFormatted,
      poInvoicesCount: poSummary.poInvoicesCount,
      reasonOfRejection: poSummary.reasonOfRejection,
    };

    const mappedBreakdownData = (invoiceData.rings_data || []).map((ring) => ({
      name: ring.ringName || ring.sectionName || '',
      milestone: ring.paymentMilestone || ring.paymentRingMilestone || '',
      length: ring.length || '',
      unitRate: ring.unitRate || '',
      invoiceAmount: ring.amount || '',
    }));

    const mappedDocuments = [];
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';

    // Add PO file if exists
    if (invoiceData.po_file_path) {
      const fileName = invoiceData.po_file_path.split('/').pop() || 'PO File.pdf';
      mappedDocuments.push({
        id: 'po-file',
        name: 'PO File',
        fileName: fileName,
        filePath: invoiceData.po_file_path,
        url: `${apiBaseUrl}/${invoiceData.po_file_path}`,
        type: 'application/pdf',
      });
    }

    // Add other documents
    if (invoiceData.other_documents_paths && Array.isArray(invoiceData.other_documents_paths)) {
      invoiceData.other_documents_paths.forEach((docPath, index) => {
        const fileName = docPath.split('/').pop() || `Document ${index + 1}`;
        const lowerPath = docPath.toLowerCase();
        const lowerFileName = fileName.toLowerCase();

        // Determine file type based on extension
        let fileType = 'application/octet-stream';
        if (lowerPath.endsWith('.pdf') || lowerFileName.endsWith('.pdf')) {
          fileType = 'application/pdf';
        } else if (lowerPath.endsWith('.xlsx') || lowerFileName.endsWith('.xlsx')) {
          fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (lowerPath.endsWith('.xls') || lowerFileName.endsWith('.xls')) {
          fileType = 'application/vnd.ms-excel';
        } else if (lowerPath.endsWith('.csv') || lowerFileName.endsWith('.csv')) {
          fileType = 'text/csv';
        } else if (lowerPath.endsWith('.msg') || lowerFileName.endsWith('.msg')) {
          fileType = 'application/vnd.ms-outlook';
        } else if (lowerPath.endsWith('.kmz') || lowerFileName.endsWith('.kmz')) {
          fileType = 'application/vnd.google-earth.kmz';
        }

        mappedDocuments.push({
          id: `other-doc-${index}`,
          name: fileName,
          fileName: fileName,
          filePath: docPath,
          // Use the approval persons authenticated endpoint for serving documents
          url: `${apiBaseUrl}/approvalPersonInvoiceManagement/invoice/document/${docPath}`,
          type: fileType,
        });
      });
    }

    return { mappedInvoiceData, mappedBreakdownData, mappedDocuments };
  };

  // Load approver roles on mount (for Planning L1 Forward To dropdown)
  useEffect(() => {
    const loadApproverRoles = async () => {
      setLoadingRoles(true);
      try {
        const roles = await getApproverRoles();
        setApproverRoles(roles);
      } catch (err) {
        console.error('Error loading approver roles:', err);
      } finally {
        setLoadingRoles(false);
      }
    };
    loadApproverRoles();
  }, []);

  // Load whether current user can start stopped invoices (Planning L4 / L5) for Stopped Invoices tab
  useEffect(() => {
    const loadCanStartStopped = async () => {
      try {
        const canStart = await getCanStartStopped();
        setUserCanStartStopped(canStart);
      } catch (err) {
        console.error('Error loading can-start-stopped:', err);
        setUserCanStartStopped(false);
      }
    };
    loadCanStartStopped();
  }, []);

  // Filter approver roles based on invoice region and invoice_type (same logic as admin details page)
  useEffect(() => {
    if (!approverRoles.length || !invoiceData) {
      setFilteredApproverRoles([]);
      return;
    }
    const normalizeInvoiceType = (val) => {
      if (!val) {
        return null;
      }
      return String(val).trim().toUpperCase().replace(/-/g, '_');
    };
    const invoiceRegion = invoiceData.region || '';
    const invoiceType = normalizeInvoiceType(invoicePayload?.invoice_type);
    const filtered = approverRoles.filter((role) => {
      if (role.category === 'RO') {
        if (role.region == null) {
          return true;
        }
        return role.region === invoiceRegion;
      }
      if (role.category === 'PLANNING') {
        const roleInvoiceType = normalizeInvoiceType(role.invoice_type);
        if (roleInvoiceType) {
          return roleInvoiceType === invoiceType;
        }
        if (!roleInvoiceType) {
          const hasMatchingTypeRole = approverRoles.some(
            (r) =>
              r.category === 'PLANNING' &&
              r.name === role.name &&
              normalizeInvoiceType(r.invoice_type) === invoiceType &&
              r.id !== role.id
          );
          return !hasMatchingTypeRole || !invoiceType;
        }
      }
      return true;
    });
    setFilteredApproverRoles(filtered);
  }, [approverRoles, invoiceData, invoicePayload]);

  // Load invoice data from passed state (or after document upload)
  useEffect(() => {
    const loadInvoiceData = async () => {
      setError(null);

      const invoiceId = invoiceSource?.id;
      const isSameInvoiceRefresh =
        invoiceId != null && invoiceId === lastLoadedInvoiceIdRef.current;
      if (!isSameInvoiceRefresh) {
        setLoading(true);
      }

      try {
        if (invoiceSource) {
          const { mappedInvoiceData, mappedBreakdownData, mappedDocuments } =
            mapInvoiceData(invoiceSource);

          setInvoiceData(mappedInvoiceData);
          setBreakdownData(mappedBreakdownData);
          setDocuments(mappedDocuments);
          setInvoicePreviewData(null);

          if (invoiceId != null) {
            lastLoadedInvoiceIdRef.current = invoiceId;
          }

          if (mappedDocuments.length > 0 && !initialDocSelectionRef.current) {
            setSelectedDocumentIndex(0);
            setSelectedDocument(mappedDocuments[0]);
            setShowDocumentPreview(true);
            initialDocSelectionRef.current = true;
          }
        } else {
          // If opened via deep link (e.g., from email), invoiceSource will be populated after
          // the details fetch completes. Avoid flashing an error while that fetch is in-flight.
          if (!isDeepLinkOpen) {
            setError('Invoice data not found. Please go back and try again.');
          }
        }
      } catch (err) {
        console.error('Error loading invoice data:', err);
        setError('Failed to load invoice data');
      } finally {
        setLoading(false);
      }
    };

    loadInvoiceData();
  }, [invoiceSource, isDeepLinkOpen, poInvoicesForSummary]);

  // Load all invoices for this PO to show count and claimed amount
  useEffect(() => {
    const poNum = (invoicePayload?.po_number || invoicePayload?.poNumber || '').trim();
    if (!poNum) {
      setPoInvoicesForSummary([]);
      return undefined;
    }

    let cancelled = false;
    const loadPoInvoices = async () => {
      try {
        const response = await getAllVendorInvoicesApprovalPerson();
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.response)
              ? response.response
              : [];
        const forPo = list.filter((inv) => (inv.po_number || inv.poNumber || '').trim() === poNum);
        if (!cancelled) {
          setPoInvoicesForSummary(forPo);
        }
      } catch (err) {
        if (!cancelled) {
          setPoInvoicesForSummary([]);
        }
      }
    };

    loadPoInvoices();
    return () => {
      cancelled = true;
    };
  }, [invoicePayload?.po_number, invoicePayload?.poNumber]);

  const handleMissingDocumentsSaved = useCallback((invoiceRow) => {
    setInvoiceSource((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        other_documents_paths: invoiceRow.other_documents_paths,
      };
    });
  }, []);

  const showAddMissingDocuments =
    invoicePayload &&
    (invoicePayload.status || '').toLowerCase() === 'pending_rejection' &&
    (invoicePayload.assigned_role?.name || '').trim().toLowerCase() === 'planning l1';

  // Check if invoice tracking should be hidden (for Ro L1, Ro L2, Ro L3 roles)
  const assignedRoleName = invoicePayload?.assigned_role?.name || '';
  const roleNameLower = assignedRoleName.toLowerCase().trim();
  const hideInvoiceTracking = ['ro l1', 'ro l2', 'ro l3'].includes(roleNameLower);

  // Fetch audit logs function - always fetch so we can show previous approver email on details card (like admins)
  const fetchAuditLogs = useCallback(async () => {
    if (!currentInvoiceId) {
      return;
    }

    try {
      setLoadingAuditLogs(true);
      const response = await getApprovalPersonInvoiceAuditLogs(currentInvoiceId);
      // Handle the API response structure: { status, message, response: [...] }
      const logs = response?.response && Array.isArray(response.response) ? response.response : [];
      setAuditLogs(logs);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  }, [currentInvoiceId]);

  // Fetch audit logs when invoice details page opens (needed for previous approver email in card)
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  // Derive approver emails from audit logs and merge into invoiceData
  useEffect(() => {
    if (!invoiceData) {
      return;
    }
    const previousApproverEmail = getPreviousApproverEmailFromAuditLogs(auditLogs);
    const currentApproverEmail = getCurrentApproverEmail(
      auditLogs,
      invoiceData.currentApprover,
      invoicePayload
    );
    setInvoiceData((prev) => {
      if (!prev) {
        return prev;
      }
      if (
        prev.previousApproverEmail === previousApproverEmail &&
        prev.currentApproverEmail === currentApproverEmail
      ) {
        return prev;
      }
      return { ...prev, previousApproverEmail, currentApproverEmail };
    });
  }, [auditLogs, invoiceData?.invoiceNumber, invoiceData?.currentApprover, invoicePayload]);

  const handleBack = () => {
    runWithUnsavedCommentsGuard(
      commentsSectionRef,
      () => navigate('/approvalPersonsInvoiceManagement'),
      { isNavigation: true }
    );
  };

  const handleApprove = async () => {
    if (!currentInvoiceId || loadingAction) {
      return;
    }
    setLoadingAction('approve');
    try {
      await approveInvoice(currentInvoiceId, comment);
      // Navigate on success
      navigate('/approvalPersonsInvoiceManagement');
    } catch (err) {
      // Error is already handled in the function
      setLoadingAction(null);
    }
  };

  const handleReject = async () => {
    if (!currentInvoiceId || loadingAction) {
      return;
    }
    const reason = (rejectReason || '').trim();
    if (!reason) {
      notify('error', 'Please enter reason of rejection');
      return;
    }
    setLoadingAction('reject');
    try {
      await rejectInvoice(currentInvoiceId, reason, comment);
      // Navigate on success
      navigate('/approvalPersonsInvoiceManagement');
    } catch (err) {
      // Error is already handled in the function
      setLoadingAction(null);
    }
  };

  // const handleRevert = async () => {
  //   if (!currentInvoiceNumber || loadingAction) {
  //     return;
  //   }
  //   setLoadingAction('revert');
  //   try {
  //     await revertInvoice(currentInvoiceNumber, comment);
  //     // Navigate on success
  //     navigate('/approvalPersonsInvoiceManagement');
  //   } catch (err) {
  //     // Error is already handled in the function
  //     setLoadingAction(null);
  //   }
  // };

  // const handleStop = async () => {
  //   if (!currentInvoiceNumber || loadingAction) {
  //     return;
  //   }
  //   setLoadingAction('stop');
  //   try {
  //     await stopInvoice(currentInvoiceNumber, comment);
  //     // Navigate on success
  //     navigate('/approvalPersonsInvoiceManagement');
  //   } catch (err) {
  //     // Error is already handled in the function
  //     setLoadingAction(null);
  //   }
  // };

  // const handleStart = async () => {
  //   if (!currentInvoiceNumber || loadingAction) {
  //     return;
  //   }
  //   setLoadingAction('start');
  //   try {
  //     await startInvoice(currentInvoiceNumber, comment);
  //     // Navigate on success
  //     navigate('/approvalPersonsInvoiceManagement');
  //   } catch (err) {
  //     // Error is already handled in the function
  //     setLoadingAction(null);
  //   }
  // };

  const handleHistory = () => {
    // Don't open modal if invoice tracking is hidden for this role
    if (hideInvoiceTracking) {
      return;
    }
    setHistoryModalOpen(true);
  };

  const handleAssignTo = async (role) => {
    if (!currentInvoiceId || assigningRole) {
      return;
    }
    await runWithUnsavedCommentsGuard(
      commentsSectionRef,
      async () => {
        setAssigningRole(true);
        try {
          const response = await apiClient.post(
            `/approvalPersonInvoiceManagement/invoice/by-id/${currentInvoiceId}/forward`,
            { assigned_to: role.id, role_name: role.name }
          );
          const successMessage = response?.data?.message || 'Invoice forwarded successfully';
          notify('success', successMessage);
          navigate('/approvalPersonsInvoiceManagement');
        } catch (err) {
          handleApiError(err);
        } finally {
          setAssigningRole(false);
        }
      },
      { actionLabel: 'Forward' }
    );
  };

  const isPlanningL1 =
    (invoicePayload?.assigned_role?.name || '').toLowerCase().trim() === 'planning l1';

  const assignedRoleForStop = (invoicePayload?.assigned_role?.name || '').toLowerCase().trim();
  const isPlanningL4OrL5 =
    assignedRoleForStop === 'planning l4' || assignedRoleForStop === 'planning l5';

  const invoiceStatus = (invoicePayload?.status || invoiceData?.status || '').toLowerCase().trim();
  const isInvoiceStopped = invoiceStatus === 'stop';
  // Start button: show for all stopped invoices when current user is Planning L4 or L5 (e.g. from Stopped Invoices tab)
  const showStartButton = isInvoiceStopped && userCanStartStopped;
  const showStopButton = isPlanningL4OrL5;
  const showStopStartSection = showStopButton || showStartButton;

  const handleStop = async () => {
    const invoiceId = invoicePayload?.id ?? invoicePayload?._originalData?.id ?? null;
    if (!invoiceId || loadingAction) {
      return;
    }
    await runWithUnsavedCommentsGuard(
      commentsSectionRef,
      async () => {
        setLoadingAction('stop');
        try {
          await stopInvoice(invoiceId, '');
          navigate('/approvalPersonsInvoiceManagement');
        } catch (err) {
          setLoadingAction(null);
        }
      },
      { actionLabel: 'Stop' }
    );
  };

  const handleStart = async () => {
    const invoiceId = invoicePayload?.id ?? invoicePayload?._originalData?.id ?? null;
    if (!invoiceId || loadingAction) {
      return;
    }
    await runWithUnsavedCommentsGuard(
      commentsSectionRef,
      async () => {
        setLoadingAction('start');
        try {
          const result = await startInvoice(invoiceId, '');
          notify('success', result?.message || 'Invoice started successfully');
          navigate('/approvalPersonsInvoiceManagement');
        } catch (err) {
          setLoadingAction(null);
        }
      },
      { actionLabel: 'Start' }
    );
  };

  const openRejectDialog = () => {
    const existingReason =
      invoicePayload?._originalData?.reason_of_rejection ||
      invoicePayload?.reason_of_rejection ||
      invoiceData?.reason_of_rejection ||
      invoiceData?.reasonOfRejection ||
      '';
    setRejectReason(existingReason || '');
    setRejectDialogOpen(true);
  };

  const handleRejectClick = () => {
    runWithUnsavedCommentsGuard(commentsSectionRef, openRejectDialog, {
      actionLabel: 'Reject',
      saveThenContinue: true,
    });
  };

  const handleApproveClick = () => {
    runWithUnsavedCommentsGuard(commentsSectionRef, handleApprove, { actionLabel: 'Approve' });
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
  };

  const handleDocumentClick = async (index, document) => {
    console.log('Document clicked:', index, document);

    // Revoke previous blob URL if it exists
    const previousBlobUrl = documentBlobUrl;
    if (previousBlobUrl) {
      revokeBlobUrl(previousBlobUrl);
    }

    // Clear previous state
    setDocumentBlobUrl(null);
    setLoadingDocument(true);
    setSelectedDocumentIndex(index);
    setSelectedDocument(document);
    setShowDocumentPreview(true);

    try {
      // If document has a blobUrl already, use it
      if (document.blobUrl) {
        setDocumentBlobUrl(document.blobUrl);
        setLoadingDocument(false);
        return;
      }

      // If document has a direct URL, use it
      if (document.url || document.documentUrl || document.fileUrl) {
        setLoadingDocument(false);
        return;
      }

      // Otherwise, fetch blob from backend
      if (document.id && currentInvoiceNumber) {
        const blobUrl = await getDocumentBlob(currentInvoiceNumber, document.id);
        setDocumentBlobUrl(blobUrl);
      }
    } catch (error) {
      console.error('Error loading document:', error);
      handleApiError(error);
    } finally {
      setLoadingDocument(false);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (documentBlobUrl) {
        revokeBlobUrl(documentBlobUrl);
      }
    };
  }, [documentBlobUrl]);

  const handleShowInvoicePreview = () => {
    setShowDocumentPreview(false);
    setSelectedDocumentIndex(null);
    setSelectedDocument(null);
  };

  if (loading || shouldBlockErrorUiForDeepLink) {
    return (
      <DashboardLayout padding={true}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error || !invoiceData) {
    return (
      <DashboardLayout padding={true}>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              margin: '0 auto',
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
            }}
          >
            <Alert severity="error">
              {error || 'Invoice data not found. Please go back and try again.'}
            </Alert>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout padding={true}>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            margin: '0 auto',
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
          }}
        >
          {/* Header */}
          <InvoiceHeader
            invoiceNumber={invoiceData.invoiceNumber}
            onBack={handleBack}
            onApprove={isViewOnlyFromAllInvoices ? undefined : handleApproveClick}
            onReject={isViewOnlyFromAllInvoices ? undefined : handleRejectClick}
            onHistory={handleHistory}
            onStop={!isViewOnlyFromAllInvoices && showStopButton ? handleStop : undefined}
            onStart={!isViewOnlyFromAllInvoices && showStartButton ? handleStart : undefined}
            showStopStart={!isViewOnlyFromAllInvoices && showStopStartSection}
            onAssignTo={!isViewOnlyFromAllInvoices && isPlanningL1 ? handleAssignTo : undefined}
            approverRoles={!isViewOnlyFromAllInvoices && isPlanningL1 ? filteredApproverRoles : []}
            loadingRoles={!isViewOnlyFromAllInvoices && isPlanningL1 ? loadingRoles : false}
            assigningRole={!isViewOnlyFromAllInvoices && isPlanningL1 ? assigningRole : false}
            loadingAction={loadingAction}
            invoiceStatus={invoicePayload?.status || invoiceData?.status || ''}
            assignedRoleName={invoicePayload?.assigned_role?.name || ''}
          />

          <Dialog
            open={rejectDialogOpen}
            onClose={() => setRejectDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Reject Invoice</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Reason of rejection is required.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                label="Reason of rejection"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Type reason..."
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setRejectDialogOpen(false)}
                disabled={loadingAction === 'reject'}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#BD1D23' }}
                onClick={async () => {
                  await handleReject();
                  setRejectDialogOpen(false);
                }}
                disabled={loadingAction === 'reject'}
              >
                {loadingAction === 'reject' ? 'Rejecting…' : 'Reject'}
              </Button>
            </DialogActions>
          </Dialog>

          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <InvoiceDetailsCard invoiceData={invoiceData} />
              <BreakdownTable
                breakdownData={breakdownData}
                nameColumnLabel={
                  (
                    invoicePayload?.invoice_type ||
                    invoicePayload?.invoice_details?.invoice_type ||
                    ''
                  )
                    .toString()
                    .toUpperCase()
                    .replace(/-/g, '_') === 'NON_FTTS'
                    ? 'Section Name'
                    : 'Ring Name'
                }
              />
              <CommentsSection
                ref={commentsSectionRef}
                invoiceId={invoicePayload?.id}
                commentByRoleId={invoicePayload?.assigned_to_role_id}
                commentByRoleName={invoicePayload?.assigned_role?.name}
                userType="approval"
              />
              <DocumentsList
                documents={documents}
                selectedIndex={selectedDocumentIndex}
                onDocumentClick={handleDocumentClick}
              />
              {showAddMissingDocuments && (
                <AddMissingDocumentsSection
                  invoiceNumber={currentInvoiceNumber}
                  onSaved={handleMissingDocumentsSaved}
                  disabled={loading}
                />
              )}
            </Grid>

            {/* Right Column - Show Document Preview or Invoice Preview */}
            <Grid item xs={12} md={6}>
              {showDocumentPreview && selectedDocument ? (
                <DocumentPreview
                  key={`doc-preview-${selectedDocumentIndex}-${selectedDocument.id || selectedDocumentIndex}-${documentBlobUrl || selectedDocument.url || ''}`} // Force re-render when document changes
                  documentUrl={(() => {
                    // Determine if this is an Excel/CSV/MSG file
                    const isExcelOrCSVOrMsg =
                      selectedDocument.type ===
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      selectedDocument.type === 'application/vnd.ms-excel' ||
                      selectedDocument.type === 'text/csv' ||
                      selectedDocument.type === 'application/vnd.ms-outlook' ||
                      /\.(xlsx|xls|csv|msg)$/i.test(
                        selectedDocument.fileName || selectedDocument.name || ''
                      );

                    // Get the base URL
                    let baseUrl =
                      documentBlobUrl ||
                      selectedDocument.blobUrl ||
                      selectedDocument.url ||
                      selectedDocument.documentUrl ||
                      selectedDocument.fileUrl ||
                      // Only construct URL from ID if it's a real backend ID (not frontend-generated like 'other-doc-X')
                      (selectedDocument.id &&
                      !selectedDocument.id.startsWith('other-doc-') &&
                      !selectedDocument.id.startsWith('po-file')
                        ? `${process.env.REACT_APP_API_URL}/vendor/invoice/${currentInvoiceNumber}/documents/${selectedDocument.id}`
                        : null);

                    // For Excel/CSV/MSG files, add ?format=pdf to convert to PDF for preview
                    if (isExcelOrCSVOrMsg && baseUrl && !baseUrl.includes('blob:')) {
                      const separator = baseUrl.includes('?') ? '&' : '?';
                      baseUrl = `${baseUrl}${separator}format=pdf`;
                    }

                    return baseUrl;
                  })()}
                  downloadUrl={(() => {
                    // For downloads, use the original file URL (without PDF conversion)
                    return (
                      documentBlobUrl ||
                      selectedDocument.blobUrl ||
                      selectedDocument.url ||
                      selectedDocument.documentUrl ||
                      selectedDocument.fileUrl ||
                      (selectedDocument.id &&
                      !selectedDocument.id.startsWith('other-doc-') &&
                      !selectedDocument.id.startsWith('po-file')
                        ? `${process.env.REACT_APP_API_URL}/vendor/invoice/${currentInvoiceNumber}/documents/${selectedDocument.id}`
                        : null)
                    );
                  })()}
                  documentName={
                    selectedDocument.name ||
                    selectedDocument.fileName ||
                    selectedDocument.documentName ||
                    'Document'
                  }
                  documentType={(() => {
                    // For Excel/CSV/MSG files being previewed as PDF, return PDF type
                    const isExcelOrCSVOrMsg =
                      selectedDocument.type ===
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      selectedDocument.type === 'application/vnd.ms-excel' ||
                      selectedDocument.type === 'text/csv' ||
                      selectedDocument.type === 'application/vnd.ms-outlook' ||
                      /\.(xlsx|xls|csv|msg)$/i.test(
                        selectedDocument.fileName || selectedDocument.name || ''
                      );

                    if (isExcelOrCSVOrMsg) {
                      return 'application/pdf'; // Treat as PDF for preview
                    }
                    return selectedDocument.type || selectedDocument.mimeType || 'application/pdf';
                  })()}
                  isLoading={loadingDocument}
                />
              ) : (
                invoicePreviewData && <InvoicePreview invoicePreviewData={invoicePreviewData} />
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* History Modal */}
      <InvoiceHistoryModal
        open={historyModalOpen}
        onClose={handleCloseHistoryModal}
        invoiceId={currentInvoiceId}
        auditLogs={transformAuditLogsForTimeline(auditLogs)}
        isLoadingAuditLogs={loadingAuditLogs}
        onRefresh={fetchAuditLogs}
      />
    </DashboardLayout>
  );
};

export default InvoiceApproveRejectForm;
